import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/hooks/useWorkspace";

interface LowStockProduct {
  id: string;
  name: string;
  sku: string;
  currentStock: number;
  minStock: number;
}

export function LowStockAlert() {
  const [products, setProducts] = useState<LowStockProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchData();
  }, [workspaceId]);

  const fetchData = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data: inventory } = await supabase
        .from("inventory")
        .select(`quantity, product_id, products(id, name, sku, min_stock, workspace_id)`)
        .eq("workspace_id", workspaceId)
        .order("quantity", { ascending: true });

      const lowStockProducts: LowStockProduct[] = [];

      inventory?.forEach((item) => {
        const product = item.products as any;
        const currentStock = item.quantity || 0;
        const minStock = product?.min_stock || 0;

        if (currentStock <= minStock && product) {
          lowStockProducts.push({
            id: product.id,
            name: product.name,
            sku: product.sku,
            currentStock,
            minStock,
          });
        }
      });

      setProducts(lowStockProducts.slice(0, 5));
    } catch (error) {
      console.error("Error fetching low stock products:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container">
        <div className="mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" /> Stock Bajo
          </h3>
          <p className="text-sm text-muted-foreground">Productos que necesitan reposición</p>
        </div>
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="chart-container animate-slide-in-up">
      <div className="mb-4">
        <h3 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" /> Stock Bajo
        </h3>
        <p className="text-sm text-muted-foreground">Productos que necesitan reposición</p>
      </div>
      {products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <Package className="h-12 w-12 mb-2 opacity-50" />
          <p>Todo el stock está en niveles óptimos</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product) => {
            const isCritical = product.currentStock === 0;
            return (
              <div key={product.id} className={cn("flex items-center gap-4 p-3 rounded-lg transition-colors", isCritical ? "bg-destructive/10 border border-destructive/30" : "bg-warning/10 border border-warning/30")}>
                <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", isCritical ? "bg-destructive/20 text-destructive" : "bg-warning/20 text-warning")}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                  <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
                </div>
                <div className="text-right">
                  <p className={cn("text-sm font-bold", isCritical ? "text-destructive" : "text-warning")}>{product.currentStock} / {product.minStock}</p>
                  <p className="text-xs text-muted-foreground">{isCritical ? "Sin stock" : "Stock bajo"}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
